/**
* Page Routes
*/
const PageRoutes = {
  page: (id) => `/pages/${id}`,
  sitemap: '/page/sitemap'
}

/**
* Invalidate Page Cache
* @param {String} id - Optional page ID
*/
export const invalidatePage = {
  page(id) {
    if (id) {
      return useInvalidateCache(PageRoutes.page(id))
    }
    return Promise.resolve()
  },

  sitemap() {
    return useInvalidateCache(PageRoutes.sitemap)
  }
}

/**
* useGetPage
* @param {String} slug 
* @returns {Promise<Object>}
*/
export function useGetPage(slug) {
  let pathName = ''
  let id = 1
  const urlMatch = String(slug).match(/^(.*?)-(\d+)$/)

  if (urlMatch) {
    pathName = urlMatch[1]
    id = urlMatch[2]
  } else {
    id = slug
  }

  if (!id) {
    throw createError({
      fatal: true,
      statusCode: 404,
      message: 'Not Found'
    })
  }

  return useCustomFetch(PageRoutes.page(id), {
    headers: {
      'Authorization': 'Public'
    },
    lazy: false,
    onResponse({ response }) {  
      if (response.status === 200 && (pathName && !response._data.page?.url_path_name.includes(pathName)) || response._data.page?.status !== 'published') {
        throw createError({
          fatal: true,
          statusCode: 404,
          message: 'Not Found'
        })
      }
        
      return response
    }
  })
}

/**
* useGetSitemap
* @returns {Promise<Object>}
*/
export function useGetSitemap() {
  const { $i18n: i18n } = useNuxtApp()

  return useCustomFetch(PageRoutes.sitemap, {
    headers: {
      'Authorization': 'Public'
    },
    lazy: false,
    transform(input) {
      const sitemapList = input.data.sitemapList

      // Categories and Products
      const products = []
      for (const i of sitemapList.dynamicProductData.typeIdsOrder) {
        const types = sitemapList.dynamicProductData.typesData[i]
        const subType = sitemapList.dynamicProductData.productsByType[i]

        const list = []
        for (const n in subType) {
          const pathName = subType[n].urlPathName.split('/')[subType[n].urlPathName.split('/').length - 1]

          list.push({
            name: subType[n].title,
            slug: '/product/' + pathName + '-' + subType[n].id
          })
        }

        const categoryPathName = types.href.split('/')[types.href.split('/').length - 1]
        if (categoryPathName === 'products') {
          products.push({
            name: types.name,
            slug: '/catalog/products',
            list
          })
        } else {
          products.push({
            name: types.name,
            slug: '/catalog/' + categoryPathName + '-' + types.id,
            list
          })
        }
      }

      // Services
      const services = [{
        name: '',
        slug: '',
        list: []
      }]
      for (const subType of sitemapList.servicesData.list) {
        let type = false

        if (subType.type === 'page' || subType.type === 'pages') {
          type = 'page'
        } else if (subType.type === 'catalog') {
          type = 'catalog'
        }

        const slug = subType.href?.replace(/\/$/, '').split('/')[subType.href?.replace(/\/$/, '').split('/').length - 1] + '-' + subType.id

        if (type) {
          const pathName = `/${type}/${slug}`

          services[0].list.push({
            name: subType.title,
            slug: pathName
          })
        }
      }

      // Solutions
      const solutions = [{
        name: '',
        slug: '',
        list: []
      }]
      for (const subType of sitemapList.solutionsData.list) {
        let type = false

        if (subType.type === 'page' || subType.type === 'pages') {
          type = 'page'
        } else if (subType.type === 'catalog') {
          type = 'catalog'
        }

        const slug = subType.href?.replace(/\/$/, '').split('/')[subType.href?.replace(/\/$/, '').split('/').length - 1] + '-' + subType.id

        if (type) {
          const pathName = `/${type}/${slug}`

          solutions[0].list.push({
            name: subType.title,
            slug: pathName
          })
        }
      }

      // Support
      const support = [{
        name: '',
        slug: '',
        list: []
      }]
      for (const subType of sitemapList.supportData.list) {
        let type = false

        if (subType.type === 'page' || subType.type === 'pages') {
          type = 'page'
        } else if (subType.type === 'catalog') {
          type = 'catalog'
        }

        const slug = subType.href?.replace(/\/$/, '').split('/')[subType.href?.replace(/\/$/, '').split('/').length - 1] + '-' + subType.id

        if (type) {
          const pathName = `/${type}/${slug}`

          support[0].list.push({
            name: subType.title,
            slug: pathName
          })
        }
      }

      // Return
      return [
        {
          name: i18n?.t('products'),
          slug: '/catalog/products',
          classes: 'products s12 m12 l12',
          list: products
        },
        {
          name: i18n?.t('services'),
          slug: '',
          classes: 'services s12 m4 l4',
          list: services
        },
        {
          name: i18n?.t('solutions'),
          slug: '',
          classes: 'solutions s12 m4 l4',
          list: solutions
        },
        {
          name: i18n?.t('support'),
          slug: '',
          classes: 'support s12 m4 l4',
          list: support
        }
      ].filter(n => n.list[0].list.length > 0)
    },
    onResponse({ response }) {   
      return response._data
    }
  })
}